import React from 'react';
import Select from "@/components/molecules/Select";
import { useEffect, useState } from "react";
import Button from "@/components/atoms/Button";

interface Option {
  id: string | number;
  label: string;
  countries: { id: string | number; name: string, status: boolean; children: { id: string | number; name: string }[] }[];
}

interface FilterOption {
  id: string;
  label: string;
  options: { value: string | number; label: string; countries?: { id: string | number; name: string }[] }[];
  selected?: (string | number)[];
}

interface UserFilterProps {
  filters: FilterOption[];
  selectedFilters: { [key: string]: (string | number)[] | null };
  onFilterChange: (filterId: string, value: (string | number)[] | null) => void;
}

const UserFilter: React.FC<UserFilterProps> = ({ filters, selectedFilters, onFilterChange }) => {
  const [isVisibleCountry, setIsVisibleCountry] = useState<boolean>(false);
  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  
  const handleChange = (filterId: string, value: (string | number)[]) => {
    onFilterChange(filterId, value.length ? value : null);
  
    if (value.length && filterId === 'account_ids') {
      setIsVisibleCountry(true)
      const accountFilter = filters.find(filter => filter.id === 'account_ids');
      if (accountFilter) {
        const filteredOptions = accountFilter.options
          .filter(option => value.includes(option.value.toString()))
          .map(option => ({
            id: option.value,
            label: option.label,
            countries: option.countries,
          }));
       
        setCountryOptions(filteredOptions);
      } else {
        setCountryOptions([]);
        onFilterChange('country_ids', null);
      }

    } 
    
    if (value.length == 0 && filterId === 'account_ids'){
      setIsVisibleCountry(false);
      setCountryOptions([]);
      onFilterChange('country_ids', null);
    }
  };

  const getSelectOptions = (options: Option[]) => {
    return options.flatMap(option => {
      // Check if any country in the `countries` array has non-empty `children`
      const hasChildren = option.countries.some(country => country.children.length > 0);
  
      if (!hasChildren) {
        // Case when `children` is empty: disable the option and include its `countries`
        return [
          {
            label: `${option.label} Countries`,
            value: `account-${option.id}`,
            disabled: true,
          },
          ...option.countries.map(country => ({
            label: country.name,
            value: country.id.toString(),
            disabled: false,
          }))
        ];
      }
  
      // Case when `children` is not empty: disable the option and its `countries`,
      // but include `children` as selectable options without disabling
      return [
        {
          label: `${option.label} Countries`,
          value: `account-${option.id}`,
          disabled: true,
        },
        ...option.countries.flatMap(country => [
          {
            label: `Region: ${country.name}`,
            value: country.id.toString(),
            disabled: true,
          },
          ...country.children.map(child => ({
            label: child.name,
            value: child.id.toString(),
            disabled: false,
          }))
        ])
      ];
    });
  };

  return (
    <div className="mb-4 flex space-x-4 items-end">
      {filters.map(filter => (
        <div key={filter.id} className="flex flex-col w-60">
          <label htmlFor={filter.id} className="mr-2 text-sm">{filter.label}:</label>
          <Select
            multiple
            options={
              filter.options.map((option) => ({
                label: option.label,
                value: option.value.toString(),
              })) || []
            }
            value={filter.selected || selectedFilters[filter.id] || []}
            onChange={(value) => handleChange(filter.id, value)}
            className= 'w-100'
            enabledSearch={true}
          />
        </div>
      ))}
      {isVisibleCountry && (
        <div className="flex flex-col w-60">
          <label htmlFor="countries" className="mr-2 text-sm">By Country:</label>
          <Select
            multiple
            options={getSelectOptions(countryOptions)}
            value={selectedFilters['country_ids'] || []}
            onChange={(value) => onFilterChange('country_ids', value)}
            className= 'w-100'
            enabledSearch={true}
          />
        </div>
      )} 
    </div>
  );
};

export default UserFilter;
